import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Heart, User, Search, Menu, Sun, Moon, X, Truck } from "lucide-react";
import { useTheme } from "next-themes";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice, img } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "New Arrivals", to: "/shop?tag=new" },
  { label: "Best Sellers", to: "/shop?tag=best_sellers" },
  { label: "Christian", to: "/shop?category=Christian Products" },
  { label: "About", to: "/about" },
];

function CartSheet() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal, cartCount } = useStore();
  const nav = useNavigate();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-sheet">
        <SheetHeader><SheetTitle className="font-display text-2xl">Your Bag ({cartCount})</SheetTitle></SheetHeader>
        {cart.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
            <ShoppingBag className="h-12 w-12" /><p>Your bag is empty.</p>
            <Button onClick={() => { setCartOpen(false); nav("/shop"); }} className="rounded-none bg-[#D4AF37] text-black hover:brightness-110">Start Shopping</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-5 overflow-y-auto py-4 hide-scrollbar">
              {cart.map((i) => (
                <div key={i.id} className="flex gap-4" data-testid={`cart-item-${i.id}`}>
                  <img src={img(i.image)} alt={i.name} className="h-24 w-20 object-cover bg-[#F5F1E8]" />
                  <div className="flex flex-1 flex-col">
                    <p className="font-display leading-tight">{i.name}</p>
                    <p className="text-sm text-muted-foreground">{formatPrice(i.price)}</p>
                    <div className="mt-auto flex items-center gap-3">
                      <div className="flex items-center border border-border">
                        <button className="px-2 py-1" onClick={() => updateQty(i.id, i.quantity - 1)} data-testid={`cart-dec-${i.id}`}>−</button>
                        <span className="px-2 text-sm">{i.quantity}</span>
                        <button className="px-2 py-1" onClick={() => updateQty(i.id, i.quantity + 1)} data-testid={`cart-inc-${i.id}`}>+</button>
                      </div>
                      <button className="text-xs text-muted-foreground underline" onClick={() => removeFromCart(i.id)} data-testid={`cart-remove-${i.id}`}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex justify-between font-display text-lg"><span>Subtotal</span><span>{formatPrice(cartTotal)}</span></div>
              <p className="text-xs text-muted-foreground mt-1">Free shipping over $75 · Taxes at checkout</p>
              <Button data-testid="cart-checkout-btn" onClick={() => { setCartOpen(false); nav("/checkout"); }}
                className="mt-4 w-full rounded-none bg-[#D4AF37] text-black font-semibold hover:brightness-110">Secure Checkout</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function Layout({ children }) {
  const { cartCount, wishlist, setCartOpen } = useStore();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/shop?search=${encodeURIComponent(q)}`); setMobileOpen(false); } };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-black text-[#F5F1E8] text-center text-xs tracking-[0.2em] uppercase py-2.5">
        Free worldwide shipping over $75 · The marketplace, reimagined
      </div>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 dark:bg-black/65 border-b border-black/5 dark:border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex h-20 items-center gap-4">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
            <Link to="/" data-testid="logo-link" className="font-display text-3xl font-bold tracking-tight">GULIT</Link>
            <nav className="hidden lg:flex items-center gap-8 ml-8">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                  className="text-sm tracking-wide hover:text-[#D4AF37] transition-colors">{n.label}</Link>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-1 sm:gap-3">
              <form onSubmit={submit} className="hidden md:flex items-center relative">
                <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
                <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products"
                  className="w-44 rounded-none border-0 border-b border-border bg-transparent pl-9 focus-visible:ring-0" />
              </form>
              <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle" className="grid h-10 w-10 place-items-center hover:text-[#D4AF37]">
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <Link to="/track-order" className="hidden sm:grid h-10 w-10 place-items-center hover:text-[#D4AF37]" data-testid="track-link"><Truck className="h-5 w-5" /></Link>
              <Link to={user ? "/account" : "/login"} className="grid h-10 w-10 place-items-center hover:text-[#D4AF37]" data-testid="account-link"><User className="h-5 w-5" /></Link>
              <Link to="/wishlist" className="relative grid h-10 w-10 place-items-center hover:text-[#D4AF37]" data-testid="wishlist-link">
                <Heart className="h-5 w-5" />
                {wishlist.length > 0 && <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-[#D4AF37] text-[9px] font-bold text-black">{wishlist.length}</span>}
              </Link>
              <button onClick={() => setCartOpen(true)} className="relative grid h-10 w-10 place-items-center hover:text-[#D4AF37]" data-testid="cart-open-btn">
                <ShoppingBag className="h-5 w-5" />
                {cartCount > 0 && <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-[#D4AF37] text-[9px] font-bold text-black">{cartCount}</span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-background lg:hidden" data-testid="mobile-menu">
          <div className="flex h-20 items-center justify-between px-4 border-b border-border">
            <span className="font-display text-2xl font-bold">GULIT</span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-close"><X className="h-6 w-6" /></button>
          </div>
          <form onSubmit={submit} className="p-4 border-b border-border"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="rounded-none" /></form>
          <nav className="flex flex-col p-4">
            {NAV.map((n) => <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`} className="py-3 font-display text-xl border-b border-border">{n.label}</Link>)}
            <Link to="/track-order" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-track-order" className="py-3 font-display text-xl border-b border-border">Track Order</Link>
          </nav>
        </div>
      )}

      <main className="flex-1">{children}</main>
      <CartSheet />
      <Footer />
    </div>
  );
}

function Footer() {
  const links = {
    Shop: [["All Products", "/shop"], ["New Arrivals", "/shop?tag=new"], ["Best Sellers", "/shop?tag=best_sellers"], ["On Sale", "/shop?tag=sale"]],
    Company: [["About GULIT", "/about"], ["Contact", "/contact"], ["FAQ", "/faq"], ["Track Order", "/track-order"]],
    Policies: [["Privacy Policy", "/privacy"], ["Shipping Policy", "/shipping"], ["Return Policy", "/returns"], ["Terms", "/terms"]],
  };
  return (
    <footer className="bg-black text-[#F5F1E8] mt-24">
      <div className="mx-auto max-w-7xl px-6 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h3 className="font-display text-3xl font-bold">GULIT</h3>
          <p className="mt-4 max-w-xs text-sm text-[#A3A3A3] leading-relaxed">A neighborhood marketplace, reimagined for the world. Trusted goods, warmth, and timeless craft delivered to your door.</p>
        </div>
        {Object.entries(links).map(([title, items]) => (
          <div key={title}>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">{title}</p>
            <ul className="space-y-2.5">{items.map(([l, to]) => <li key={l}><Link to={to} className="text-sm text-[#A3A3A3] hover:text-white transition-colors">{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-[#A3A3A3]">© {new Date().getFullYear()} GULIT. All rights reserved · Stripe · PayPal · USPS · UPS · FedEx · DHL</div>
    </footer>
  );
}
